import {Alert, ButtonActionProps, openConfirmDialog} from 'platform/components';
import {VStack, Show} from 'platform/foundation';

import {ReactNode} from 'react';

import {isNotNil} from 'ramda';

import {SimilarVehicleResponseBody} from '@dms/api/shared';
import {useReRegisterVehicleForSaleMutation, VinDecoderResponseBody} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {businessCaseRoutes} from '@dms/routes';
import {
  handleApiError,
  VinDecoderField as SharedVinDecoder,
  VehicleRecommendationType,
  VehicleSelectionType,
} from '@dms/shared';

import {buildArray, composePath, openInNewTab} from 'shared/web';

import {VEHICLE_FORM_EVENTS} from '../../core/machine/constants/VEHICLE_FORM_EVENTS';
import {BaseVehicle} from '../../core/types/BaseVehicle';
import {useBusinessCaseTakeover} from '../hooks/useBusinessCaseTakeover';
import {useSimilarVehicleHandler} from '../hooks/useSimilarVehicleHandler';
import {useVehicleFormContext} from '../hooks/useVehicleFormContext';

interface VinDecoderFieldProps {
  variant?: VehicleRecommendationType;
  shouldRecommendVehicles?: boolean;
  isDisabled?: boolean;
  hideChooseButton?: boolean;
  isPurchaseMode?: boolean;
  onSelect?: (vehicle: SimilarVehicleResponseBody, selectionType?: VehicleSelectionType) => void;
}

export function VinDecoderField(props: VinDecoderFieldProps) {
  const {control, formApi, sendMachineEvent, mandatoryFields, vehicleId} = useVehicleFormContext();

  const similarVehicleHandler = useSimilarVehicleHandler();
  const [reRegisterVehicleForSale] = useReRegisterVehicleForSaleMutation();
  const [takeoverBusinessCase] = useBusinessCaseTakeover();

  const handleDecodedData = (decodedData?: VinDecoderResponseBody) => {
    if (decodedData) {
      sendMachineEvent({type: VEHICLE_FORM_EVENTS.VIN_DECODED_SUCCESS, decodedData});
    } else {
      sendMachineEvent({
        type: VEHICLE_FORM_EVENTS.VIN_DECODE_FAILED,
        error: i18n.t('general.labels.somethingWentWrong'),
      });
    }
  };

  const renderSimilarVehicleAlert = (): ReactNode => {
    const hasExactVinMatch = similarVehicleHandler.hasExactSameVin(formApi.getValues('vin'));

    if (
      !similarVehicleHandler.state ||
      (!hasExactVinMatch && similarVehicleHandler.state.similarVehicles.length === 0)
    ) {
      return null;
    }

    if (hasExactVinMatch) {
      return (
        <Alert
          variant="error"
          title={i18n.t('entity.vehicle.labels.VINExists')}
          message={i18n.t('entity.vehicle.labels.cannotCreateSameVIN')}
          onClose={() => similarVehicleHandler.reset()}
        />
      );
    }

    return null;
  };

  const vehicleSelectProps = props.shouldRecommendVehicles
    ? {
        onVehicleSelect: (
          vehicle: SimilarVehicleResponseBody,
          selectionType?: VehicleSelectionType
        ) => {
          props.onSelect?.(vehicle, selectionType);
        },
        shouldRecommendVehiclesFor: props.variant,
        hideChooseButton: props.hideChooseButton,
      }
    : {};

  return (
    <VStack spacing={4}>
      <SharedVinDecoder<BaseVehicle>
        {...vehicleSelectProps}
        control={control}
        formApi={formApi}
        registrationPlateField={{
          name: 'registrationPlate',
          label: i18n.t('entity.vehicle.labels.licensePlate'),
          isRecommended: mandatoryFields.includes('registrationPlate'),
        }}
        vinField={{
          name: 'vin',
          isDisabled: props.isDisabled,
          onChange: (value) => {
            const registrationPlateValue = formApi.getValues('registrationPlate');

            similarVehicleHandler.handleVinAndPlateChange({
              vin: value ?? undefined,
              registrationPlate: registrationPlateValue ?? undefined,
            });
          },
          label: i18n.t('entity.vehicle.labels.vin'),
          isRecommended: mandatoryFields.includes('vin'),
          onVinDecode() {
            sendMachineEvent({type: VEHICLE_FORM_EVENTS.DECODE_VIN});
          },
        }}
        excludeFromResultVehicleIds={vehicleId ? [vehicleId] : undefined}
        onDecodedData={handleDecodedData}
        additionalActions={(vehicle) => {
          const existingBusinessCase = similarVehicleHandler.getExistingBusinessCase(vehicle.id);
          const canTakeover =
            props.isPurchaseMode &&
            existingBusinessCase &&
            existingBusinessCase.branchId !== similarVehicleHandler.activeBranchId;

          const canReRegister =
            !props.isPurchaseMode && similarVehicleHandler.isReRegisterable(vehicle.id);

          return buildArray<ButtonActionProps>()
            .when(isNotNil(existingBusinessCase), {
              type: 'button',
              leftIcon: 'action/launch',
              variant: 'outlined',
              title: i18n.t('entity.businessCase.labels.view'),
              onClick: () =>
                openInNewTab(
                  composePath(businessCaseRoutes.overview, {
                    params: {id: existingBusinessCase?.id ?? ''},
                  })
                ),
            })
            .when(canTakeover, {
              type: 'button',
              variant: 'secondary',
              title: i18n.t('entity.businessCase.labels.takeover'),
              onClick: () => takeoverBusinessCase(vehicle.id),
            })
            .when(canReRegister, {
              type: 'button',
              variant: 'secondary',
              title: i18n.t('entity.vehicle.actions.reregister'),
              onClick: () => {
                openConfirmDialog({
                  text: i18n.t('page.vehicle.labels.confirmReRegister'),
                  onConfirm: async () => {
                    if (!props.isPurchaseMode) {
                      await reRegisterVehicleForSale({vehicleId: vehicle.id})
                        .unwrap()
                        .catch(handleApiError);
                    }
                    props.onSelect?.(vehicle);
                  },
                });
              },
            });
        }}
      />

      <Show when={similarVehicleHandler.state !== null}>{renderSimilarVehicleAlert()}</Show>
    </VStack>
  );
}
